import React from "react";
import { AlertTriangle, CheckCircle, XCircle } from "lucide-react";

const DuplicateResolver = ({
  theme,
  language,
  duplicates = [],
  duplicateSelections,
  onSelect,
  onUpdate,
  onResolveBulk,
  isResolvingDuplicates,
}) => {
  const isAr = language === "ar";

  if (!duplicates || duplicates.length === 0) {
    return (
      <div
        style={{
          padding: "32px",
          textAlign: "center",
          color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af",
        }}
      >
        <CheckCircle
          size={40}
          style={{ margin: "0 auto 12px", color: "#10b981" }}
        />
        <p style={{ margin: 0, fontWeight: "600" }}>
          {isAr ? "لا توجد تعارضات!" : "No duplicates remaining!"}
        </p>
      </div>
    );
  }

  const btnBase = {
    padding: "6px 14px",
    borderRadius: "8px",
    border: "none",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Bulk actions */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "12px 16px",
          borderRadius: "10px",
          background:
            theme === "dark"
              ? "rgba(245, 158, 11, 0.1)"
              : "rgba(254, 243, 199, 0.8)",
          border:
            theme === "dark"
              ? "1px solid rgba(245, 158, 11, 0.25)"
              : "1px solid rgba(245, 158, 11, 0.3)",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: theme === "dark" ? "#fbbf24" : "#92400e",
            fontWeight: "600",
            fontSize: "13px",
          }}
        >
          <AlertTriangle size={16} />
          {isAr
            ? `${duplicates.length} تعارض بحاجة لحل`
            : `${duplicates.length} duplicate(s) need resolution`}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => onResolveBulk("existing")}
            disabled={isResolvingDuplicates}
            style={{
              ...btnBase,
              background:
                theme === "dark"
                  ? "rgba(59, 130, 246, 0.3)"
                  : "rgba(219, 234, 254, 0.9)",
              color: theme === "dark" ? "#93c5fd" : "#1d4ed8",
              opacity: isResolvingDuplicates ? 0.6 : 1,
              cursor: isResolvingDuplicates ? "not-allowed" : "pointer",
            }}
          >
            {isAr ? "الكل: الحالي" : "Keep All Existing"}
          </button>
          <button
            onClick={() => onResolveBulk("incoming")}
            disabled={isResolvingDuplicates}
            style={{
              ...btnBase,
              background:
                theme === "dark"
                  ? "rgba(16, 185, 129, 0.3)"
                  : "rgba(209, 250, 229, 0.9)",
              color: theme === "dark" ? "#6ee7b7" : "#065f46",
              opacity: isResolvingDuplicates ? 0.6 : 1,
              cursor: isResolvingDuplicates ? "not-allowed" : "pointer",
            }}
          >
            {isAr ? "الكل: الجديد" : "Use All Incoming"}
          </button>
        </div>
      </div>

      {/* Individual duplicates */}
      {duplicates.map((dup, idx) => {
        const selected = duplicateSelections?.[idx];
        return (
          <div
            key={idx}
            style={{
              borderRadius: "12px",
              border:
                theme === "dark"
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "10px 16px",
                background:
                  theme === "dark"
                    ? "rgba(55, 65, 81, 0.6)"
                    : "rgba(248, 250, 252, 0.9)",
                fontSize: "12px",
                fontWeight: "700",
                color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                {isAr ? "الموظف:" : "Employee:"} {dup.employeeId} —{" "}
                {dup.date}
              </span>
              <button
                onClick={() => onUpdate(idx, dup, selected)}
                disabled={!selected || isResolvingDuplicates}
                style={{
                  ...btnBase,
                  background:
                    selected
                      ? theme === "dark"
                        ? "rgba(16, 185, 129, 0.3)"
                        : "#d1fae5"
                      : theme === "dark"
                        ? "rgba(55,65,81,0.5)"
                        : "#e5e7eb",
                  color: selected
                    ? theme === "dark" ? "#6ee7b7" : "#065f46"
                    : theme === "dark" ? "rgba(255,255,255,0.4)" : "#9ca3af",
                  cursor:
                    !selected || isResolvingDuplicates
                      ? "not-allowed"
                      : "pointer",
                  opacity: !selected || isResolvingDuplicates ? 0.6 : 1,
                }}
              >
                <CheckCircle size={12} />
                {isAr ? "حل" : "Resolve"}
              </button>
            </div>

            {/* Comparison */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0",
              }}
            >
              {/* Existing */}
              <RecordCard
                theme={theme}
                label={isAr ? "السجل الحالي" : "Existing Record"}
                record={dup.existing}
                isSelected={selected === "existing"}
                onSelect={() => onSelect(idx, "existing")}
                accent="#3b82f6"
                icon={<XCircle size={14} />}
              />
              {/* Incoming */}
              <RecordCard
                theme={theme}
                label={isAr ? "السجل الجديد" : "Incoming Record"}
                record={dup.incoming}
                isSelected={selected === "incoming"}
                onSelect={() => onSelect(idx, "incoming")}
                accent="#10b981"
                icon={<CheckCircle size={14} />}
                borderLeft
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RecordCard = ({
  theme,
  label,
  record,
  isSelected,
  onSelect,
  accent,
  icon,
  borderLeft,
}) => {
  return (
    <div
      onClick={onSelect}
      style={{
        padding: "12px 16px",
        cursor: "pointer",
        background: isSelected
          ? theme === "dark"
            ? `${accent}22`
            : `${accent}11`
          : "transparent",
        borderLeft: borderLeft
          ? theme === "dark"
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.06)"
          : "none",
        borderTop:
          theme === "dark"
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid rgba(0,0,0,0.05)",
        outline: isSelected ? `2px solid ${accent}` : "2px solid transparent",
        outlineOffset: "-2px",
        transition: "all 0.2s ease",
        borderRadius: borderLeft ? "0 0 12px 0" : "0 0 0 12px",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          fontWeight: "700",
          color: accent,
          marginBottom: "8px",
          display: "flex",
          alignItems: "center",
          gap: "5px",
        }}
      >
        {icon}
        {label}
      </div>
      {record ? (
        <RecordFields record={record} theme={theme} />
      ) : (
        <span
          style={{
            fontSize: "12px",
            color: theme === "dark" ? "rgba(255,255,255,0.4)" : "#9ca3af",
          }}
        >
          —
        </span>
      )}
    </div>
  );
};

const RecordFields = ({ record, theme }) => {
  const color = theme === "dark" ? "rgba(255,255,255,0.75)" : "#475569";
  const labelColor = theme === "dark" ? "rgba(255,255,255,0.45)" : "#9ca3af";
  const row = (label, value) => (
    <div
      key={label}
      style={{ display: "flex", gap: "6px", fontSize: "12px", marginBottom: "3px" }}
    >
      <span style={{ color: labelColor, minWidth: "70px" }}>{label}:</span>
      <span style={{ color, fontWeight: "500" }}>{value || "-"}</span>
    </div>
  );

  return (
    <div>
      {row("Check-In", record.checkIn)}
      {row("Check-Out", record.checkOut)}
      {row("Status", record.status)}
      {row("Hours", record.workingHours && record.workingHours !== "-" ? Math.round(parseFloat(record.workingHours) * 10) / 10 : "-")}
    </div>
  );
};

export default DuplicateResolver;